import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * 404 — the path matches no route in the router.
 *
 * Reached ONLY from the catch-all `*` route, which is deliberately the last child of
 * <Routes> so it can never shadow a real path. This is a statement about the URL, never
 * about the session: a signed-out user asking for a route that DOES exist gets
 * <NotAuthorized /> (401) from ProtectedRoute, not this. Conflating the two is how an
 * expired token ends up looking like a broken link and sends people chasing the wrong bug.
 *
 * Consequently this page is not auth-gated: an unknown URL is unknown whether or not you
 * are signed in.
 */
const NotFound: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  return (
    <div
      className="d-flex flex-column align-items-center justify-content-center text-center"
      style={{ minHeight: '70vh', padding: '2rem' }}
    >
      <div style={{ fontSize: 64, fontWeight: 800, color: '#1f7c50', lineHeight: 1 }}>404</div>
      <h4 style={{ color: '#101413', margin: '1rem 0 .5rem' }}>
        {t('errors.not_found_title') || 'Page not found'}
      </h4>
      <p className="text-muted" style={{ maxWidth: 520, marginBottom: '.5rem' }}>
        {t('errors.not_found_body') || 'This page does not exist. Check the address, or head back home.'}
      </p>
      <code
        style={{
          display: 'inline-block',
          background: '#f1f5f3',
          border: '1px solid #dbe5e1',
          borderRadius: 6,
          padding: '.25rem .6rem',
          fontSize: 12.5,
          color: '#5b6b66',
          marginBottom: '1.75rem',
          maxWidth: '100%',
          overflowWrap: 'anywhere',
        }}
      >
        {location.pathname}
      </code>
      <Link
        to="/"
        className="btn"
        style={{ background: '#1f7c50', color: '#fff', fontWeight: 600, padding: '.6rem 1.5rem', borderRadius: 8 }}
      >
        {t('errors.back_home') || 'Back to home'}
      </Link>
    </div>
  );
};

export default NotFound;
